import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Stack, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Button,
  FormControl, Select, MenuItem,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaidIcon from '@mui/icons-material/Paid';
import { getWaitingRoom, updateCheckin, updateAppointment, removeCheckin, callPatientCheckin, getPaymentSummaryByAppointment } from '../services/api';
import AppointmentTypeChip from './AppointmentTypeChip';
import AppointmentDialog from './AppointmentDialog';
import { PAYMENT_STATUS_COLOR } from '../constants/paymentStatus';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { useNavigate } from 'react-router-dom';

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso + (iso.endsWith('Z') ? '' : 'Z'));
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function minutesWaiting(checkedInAt) {
  if (!checkedInAt) return 0;
  const d = new Date(checkedInAt + (checkedInAt.endsWith('Z') ? '' : 'Z'));
  return Math.floor((Date.now() - d.getTime()) / 60000);
}

// Mismo cálculo que minutesWaiting pero sobre called_at — cuánto lleva el
// paciente en consulta ahora mismo. Pedido explícito del usuario
// (2026-11-04): ver en vivo cuánto dura cada consulta en curso.
function minutesInConsultation(calledAt) {
  if (!calledAt) return 0;
  const d = new Date(calledAt + (calledAt.endsWith('Z') ? '' : 'Z'));
  return Math.floor((Date.now() - d.getTime()) / 60000);
}

// Puntualidad del paciente (checked_in_at vs start_time) — distinto de
// "esperando" (checked_in_at vs ahora, que mide al consultorio/médico, no
// al paciente). Pedido explícito del usuario 2026-11-02: son dos cosas
// separadas y no deben mezclarse en un solo número. null si llegó a
// horario o antes (no hay nada que mostrar en ese caso).
function minutesLate(startTime, checkedInAt) {
  if (!startTime || !checkedInAt) return null;
  const start = new Date(startTime + (startTime.endsWith('Z') ? '' : 'Z'));
  const checkin = new Date(checkedInAt + (checkedInAt.endsWith('Z') ? '' : 'Z'));
  const diff = Math.floor((checkin.getTime() - start.getTime()) / 60000);
  return diff > 0 ? diff : null;
}

export default function WaitingRoom({ onCallPatient }) {
  const { isRecepcionista, isAdmin, isMedico, doctorId } = useAuth();
  const { features } = useClinicFeatures();
  const notify = useNotification();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [inConsultation, setInConsultation] = useState([]);
  const [loading, setLoading] = useState(true);
  // Monto acordado por turno, para que recepción cobre en el momento sin
  // abrir cada turno — pedido explícito del usuario (2026-11-04): "en la
  // sala de espera... quieren tener los números del pago". {id: {label, status}}.
  const [paymentSummary, setPaymentSummary] = useState({});
  // Con 2+ médicos atendiendo a la vez, una sola lista mezclada de
  // "esperando"/"en consulta" se vuelve ilegible — pedido explícito del
  // usuario (2026-11-04): "la sala de espera multimédicos es un lío, hay
  // que poder filtrarla por médico". Un médico arranca viendo solo lo
  // suyo (lo más útil por defecto), pero puede cambiar a "Todos" o a otro
  // médico — en consultorios chicos cualquiera cubre cualquier agenda
  // (ver nota de canManage más abajo). '' = todos.
  const [doctorFilter, setDoctorFilter] = useState(isMedico && doctorId ? doctorId : '');
  const [noteDialog, setNoteDialog] = useState(null); // { appt, note }
  const [saving, setSaving] = useState(false);
  // Gestionar pagos directo desde la sala de espera, sin ir a buscar el
  // turno a otra pantalla — pedido explícito del usuario (2026-11-04):
  // "que el recepcionista pueda accionar (gestionar pagos)... cuando
  // recepciona al paciente". Reusa el panel de Pagos ya existente dentro
  // de AppointmentDialog (isEdit=true, initialSection='billing') en vez de
  // duplicar el CRUD de pagos en un popup aparte.
  const [paymentApptDialog, setPaymentApptDialog] = useState(null); // appt o null
  // IDs con un POST /call en curso — deshabilita el botón mientras tanto
  // para evitar el doble click que disparaba dos requests concurrentes y
  // duplicaba la tarjeta en "En consulta" (reportado 2026-11-04).
  const [calling, setCalling] = useState(new Set());
  // Confirmación antes de llamar a un segundo paciente del mismo médico —
  // pedido explícito del usuario (2026-11-04): no bloquear (puede ser
  // legítimo, ej. 2 consultorios), pero sí avisar antes de hacerlo porque
  // normalmente es una equivocación de quién tocó el botón.
  const [doubleCallConfirm, setDoubleCallConfirm] = useState(null); // { appt, otherPatientName }

  // Mismas acciones para los tres roles — pedido explícito del usuario
  // (2026-11-02): en consultorios chicos cualquiera hace de recepcionista
  // de cualquier médico, así que la sala de espera no debe distinguir
  // "qué puede hacer cada rol", salvo "Llamar paciente" (abre la ficha
  // para cargar la consulta, solo tiene sentido si hay un onCallPatient
  // real pasado desde el padre). El backend ya no filtra la cola por
  // médico (acceso total a toda la clínica desde 2026-10-09).
  const canManage = isRecepcionista || isAdmin || isMedico;

  // Médicos presentes en la cola actual (de ambas listas) — se derivan de
  // los datos ya cargados, sin pegarle a /doctors aparte: no hace falta
  // mostrar en el filtro a un médico que hoy no tiene nadie esperando.
  const doctorsInQueue = useMemo(() => {
    const map = new Map();
    for (const a of [...queue, ...inConsultation]) {
      if (a.doctor && !map.has(a.doctor_id)) map.set(a.doctor_id, a.doctor);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [queue, inConsultation]);

  const filteredQueue = useMemo(
    () => (doctorFilter ? queue.filter((a) => a.doctor_id === doctorFilter) : queue),
    [queue, doctorFilter]
  );
  const filteredInConsultation = useMemo(
    () => (doctorFilter ? inConsultation.filter((a) => a.doctor_id === doctorFilter) : inConsultation),
    [inConsultation, doctorFilter]
  );

  const fetch = useCallback(async () => {
    try {
      const r = await getWaitingRoom();
      setQueue(r.data.esperando);
      setInConsultation(r.data.en_consulta);
      if (features?.billing) {
        const ids = [...r.data.esperando, ...r.data.en_consulta].map((a) => a.id);
        if (ids.length) {
          try {
            const ps = await getPaymentSummaryByAppointment(ids);
            setPaymentSummary(ps.data || {});
          } catch { /* no bloquea la sala de espera si falla el resumen de pagos */ }
        } else {
          setPaymentSummary({});
        }
      }
    } catch {
      // silently fail on poll
    } finally {
      setLoading(false);
    }
  }, [features?.billing]);

  useEffect(() => {
    fetch();
    const interval = setInterval(fetch, 20000);
    return () => clearInterval(interval);
  }, [fetch]);

  const move = async (appt, direction) => {
    // Reordena dentro de lo VISIBLE (filteredQueue), no de la cola
    // completa de todos los médicos — con el filtro por médico activo
    // (2026-11-04), "subir" tiene que moverlo respecto al compañero de
    // fila que se ve en pantalla, no respecto a un turno de otro médico
    // que ni aparece filtrado. Solo se tocan los waiting_order de los 2
    // turnos que hacen swap, el resto de la cola queda intacto.
    const visibleIdx = filteredQueue.findIndex((a) => a.id === appt.id);
    const newVisibleIdx = visibleIdx + direction;
    if (newVisibleIdx < 0 || newVisibleIdx >= filteredQueue.length) return;

    const other = filteredQueue[newVisibleIdx];
    const idx = queue.findIndex((a) => a.id === appt.id);
    const otherIdx = queue.findIndex((a) => a.id === other.id);

    // Se asignan los índices VISIBLES (no se intercambian los valores
    // crudos, que pueden venir en null si el turno nunca se reordenó) —
    // mismo criterio que el código original: posiciones consecutivas
    // dentro de lo que se está mirando.
    const newQueue = [...queue];
    newQueue[idx] = { ...newQueue[idx], waiting_order: newVisibleIdx };
    newQueue[otherIdx] = { ...newQueue[otherIdx], waiting_order: visibleIdx };
    setQueue(newQueue);

    try {
      await updateCheckin(appt.id, { waiting_order: newVisibleIdx });
      await updateCheckin(other.id, { waiting_order: visibleIdx });
    } catch {
      notify('Error al reordenar', 'error');
      fetch();
    }
  };

  const doCallPatient = async (appt) => {
    // Antes esto era puramente cosmético del lado del frontend (solo
    // navegaba, sin tocar el backend) — pedido explícito del usuario
    // (2026-11-04): persistir called_at y pasar a en_consulta para medir
    // tiempos reales de espera/duración y que recepción vea quién ya está
    // siendo atendido, aunque el médico no termine la consulta enseguida.
    setCalling((s) => new Set(s).add(appt.id));
    try {
      const r = await callPatientCheckin(appt.id);
      // Optimista: mover de la cola de espera a "en consulta" de inmediato
      // en vez de esperar el próximo poll (20s) — el backend ya refleja
      // esto en /waiting-room, así que el próximo poll solo lo confirma.
      setQueue((q) => q.filter((a) => a.id !== appt.id));
      setInConsultation((c) => (c.some((a) => a.id === r.data.id) ? c : [...c, r.data]));
      onCallPatient?.(appt);
      notify(`${appt.patient?.name} llamado a consulta`, 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al llamar paciente', 'error');
    } finally {
      setCalling((s) => { const next = new Set(s); next.delete(appt.id); return next; });
    }
  };

  const callPatient = (appt) => {
    if (calling.has(appt.id)) return;
    // ¿El médico de este turno ya tiene a otro paciente en_consulta? No lo
    // bloqueamos (puede ser legítimo: 2 consultorios, revisión rápida),
    // pero normalmente es una equivocación — confirmar antes de llamar al
    // segundo. Pedido explícito del usuario (2026-11-04).
    const other = inConsultation.find((a) => a.doctor_id === appt.doctor_id);
    if (other) {
      setDoubleCallConfirm({ appt, otherPatientName: other.patient?.name || 'otro paciente' });
      return;
    }
    doCallPatient(appt);
  };

  const markAusente = async (appt) => {
    try {
      await updateAppointment(appt.id, { state: 'ausente' });
      setQueue((q) => q.filter((a) => a.id !== appt.id));
      notify(`${appt.patient?.name} marcado como ausente`, 'warning');
    } catch (err) {
      notify(err.userMessage || 'Error', 'error');
    }
  };

  const removeFromQueue = async (appt) => {
    try {
      await removeCheckin(appt.id);
      setQueue((q) => q.filter((a) => a.id !== appt.id));
      notify('Paciente removido de la sala de espera', 'info');
    } catch (err) {
      notify(err.userMessage || 'Error', 'error');
    }
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      await updateCheckin(noteDialog.appt.id, { reception_note: noteDialog.note });
      setQueue((q) =>
        q.map((a) => a.id === noteDialog.appt.id ? { ...a, reception_note: noteDialog.note } : a)
      );
      setNoteDialog(null);
    } catch (err) {
      notify(err.userMessage || 'Error al guardar nota', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={600}>Sala de espera</Typography>
        <Chip
          label={filteredQueue.length === 0 ? 'Vacía' : `${filteredQueue.length} paciente${filteredQueue.length > 1 ? 's' : ''}`}
          size="small"
          color={filteredQueue.length > 0 ? 'primary' : 'default'}
        />
        {doctorsInQueue.length > 1 && (
          <FormControl size="small" sx={{ ml: 'auto', minWidth: 160 }}>
            <Select
              value={doctorFilter}
              displayEmpty
              onChange={(e) => setDoctorFilter(e.target.value)}
              sx={{ fontSize: 14 }}
            >
              <MenuItem value="">Todos los médicos</MenuItem>
              {doctorsInQueue.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      {filteredQueue.length === 0 ? (
        <Paper sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            {doctorFilter ? 'No hay pacientes esperando para este médico' : 'No hay pacientes esperando'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={0.5}>
          {filteredQueue.map((appt, idx) => {
            const mins = minutesWaiting(appt.checked_in_at);
            const waitColor = mins > 30 ? 'error.main' : mins > 15 ? 'warning.main' : 'text.secondary';
            // Puntualidad del paciente (vs. espera en sala, arriba) — ver
            // minutesLate más arriba.
            const late = minutesLate(appt.start_time, appt.checked_in_at);

            return (
              <Paper
                key={appt.id}
                sx={{
                  p: 1,
                  display: 'grid',
                  // Columnas de ancho FIJO (salvo "tiempos", que se
                  // estira) — antes era flex con anchos que variaban según
                  // el largo del texto de cada fila (nombre, chip de tipo),
                  // así que la columna de tiempos arrancaba en una posición
                  // X distinta en cada fila. Reportado con captura
                  // 2026-11-04: "los datos siguen estando desalineados
                  // entre sí". Grid con columnas fijas garantiza que todo
                  // arranque en la misma posición, sin importar el
                  // contenido. En mobile colapsa a una sola columna.
                  gridTemplateColumns: { xs: '1fr', sm: '20px 210px 90px 1fr auto' },
                  alignItems: 'center',
                  columnGap: 1.5,
                  rowGap: 0.5,
                  borderLeft: '4px solid',
                  borderLeftColor: idx === 0 ? 'primary.main' : 'divider',
                }}
              >
                {/* Posición — columna propia en desktop; en mobile (grid
                    colapsado a 1 columna) se antepone al nombre en vez de
                    tener su propia línea, para no gastar una fila extra. */}
                <Typography
                  sx={{
                    fontWeight: 700, fontSize: 14,
                    color: idx === 0 ? 'primary.main' : 'text.secondary',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {idx + 1}
                </Typography>

                {/* Columna paciente: nombre + médico/hora. */}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                    onClick={() => navigate(`/patients/${appt.patient_id}`)}
                    noWrap
                  >
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{idx + 1}. </Box>
                    {appt.patient?.name || 'Paciente'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {appt.doctor?.name} · {formatTime(appt.start_time)}
                  </Typography>
                </Box>

                {/* Columna tipo de turno — pedido explícito del usuario
                    2026-11-02, mismo dato que ya se ve en la agenda del día
                    y el listado de Turnos. Precargado por joinedload en el
                    backend (GET /appointments/waiting-room). */}
                <Box>
                  <AppointmentTypeChip appointmentType={appt.appointment_type} />
                </Box>

                {/* Columna tiempos: los dos indicadores, como chips del
                    mismo tratamiento visual — antes uno era texto+ícono
                    suelto y el otro un Chip con estilo distinto, quedaba
                    desprolijo (reportado con captura 2026-11-04). */}
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: '14px !important', color: `${waitColor} !important` }} />}
                    label={`${mins}min esperando`}
                    size="small"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, color: waitColor, bgcolor: 'action.hover' }}
                  />
                  {/* Llegó tarde (checked_in_at vs. start_time) — distinto
                      de "esperando", que mide al consultorio/médico (cuánto
                      lleva en sala), no al paciente (si llegó a horario).
                      Pedido explícito del usuario 2026-11-02: "una cosa es
                      la espera, y otra diferente la llegada tarde". */}
                  {late !== null && (
                    <Chip
                      label={`Llegó ${late}min tarde`}
                      size="small"
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'error.50', color: 'error.dark' }}
                    />
                  )}
                  {appt.reception_note && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontStyle: 'italic',
                        color: 'warning.dark',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      "{appt.reception_note}"
                    </Typography>
                  )}
                  {/* Monto a cobrar — pedido explícito del usuario
                      (2026-11-04): recepción tiene que cobrar en el momento
                      el valor acordado con el paciente, sin ir a buscarlo
                      al chat de WhatsApp. Clickeable: abre el turno directo
                      en la sección de Pagos para registrar el cobro. */}
                  {features?.billing && paymentSummary[appt.id] && (
                    <Chip
                      label={paymentSummary[appt.id].label}
                      size="small"
                      clickable
                      onClick={() => setPaymentApptDialog(appt)}
                      color={PAYMENT_STATUS_COLOR[paymentSummary[appt.id].status] || 'default'}
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                    />
                  )}
                </Stack>

                {/* Acciones — última columna, ancho auto. En mobile ocupa
                    su propia fila a ancho completo (columna única del
                    grid), justificada a la derecha: hasta 5 ícono-botones
                    compitiendo con el resto de la fila en una sola línea
                    quedaban demasiado apretados. */}
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ justifyContent: 'flex-end' }}
                >
                  {canManage && (
                    <>
                      <Tooltip title="Reordenar: subir">
                        <span>
                          <IconButton size="small" disabled={idx === 0} onClick={() => move(appt, -1)}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reordenar: bajar">
                        <span>
                          <IconButton size="small" disabled={idx === filteredQueue.length - 1} onClick={() => move(appt, 1)}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar nota de recepción">
                        <IconButton
                          size="small"
                          onClick={() => setNoteDialog({ appt, note: appt.reception_note || '' })}
                        >
                          <EditNoteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                  {onCallPatient && (
                    <Tooltip title="Llamar paciente e iniciar consulta">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={calling.has(appt.id)}
                          onClick={() => callPatient(appt)}
                        >
                          {calling.has(appt.id) ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {canManage && features?.billing && appt.patient_id && (
                    <Tooltip title="Cobrar / gestionar pagos">
                      <IconButton size="small" color="success" onClick={() => setPaymentApptDialog(appt)}>
                        <PaidIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canManage && (
                    <Tooltip title="Marcar ausente">
                      <IconButton size="small" color="warning" onClick={() => markAusente(appt)}>
                        <PersonOffIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {canManage && (
                    <Tooltip title="Quitar de sala de espera">
                      <IconButton size="small" onClick={() => removeFromQueue(appt)}>
                        <PersonOffIcon fontSize="small" sx={{ color: 'error.main' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Pacientes ya llamados, consulta en curso — pedido explícito del
          usuario (2026-11-04): antes "Llamar paciente" los hacía
          desaparecer sin dejar rastro; ahora quedan visibles acá con
          cronómetro de duración hasta que se finalice la HC (atendido). */}
      {filteredInConsultation.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
            <Typography variant="subtitle1" fontWeight={600}>En consulta</Typography>
            <Chip
              label={`${filteredInConsultation.length} paciente${filteredInConsultation.length > 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: 'success.main', color: 'success.contrastText' }}
            />
          </Stack>
          <Stack spacing={0.5}>
            {filteredInConsultation.map((appt) => {
              const mins = minutesInConsultation(appt.called_at);
              return (
                <Paper
                  key={appt.id}
                  sx={{
                    p: 1,
                    display: 'grid',
                    // Misma grilla de ancho fijo que la sección "esperando"
                    // (sin columna de posición, acá no hay orden) — ver
                    // comentario de esa sección sobre la desalineación
                    // reportada 2026-11-04.
                    gridTemplateColumns: { xs: '1fr', sm: '210px 90px 1fr auto' },
                    alignItems: 'center',
                    columnGap: 1.5,
                    rowGap: 0.5,
                    borderLeft: '4px solid',
                    borderLeftColor: 'success.main',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                      onClick={() => navigate(`/patients/${appt.patient_id}`)}
                      noWrap
                    >
                      {appt.patient?.name || 'Paciente'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {appt.doctor?.name}
                    </Typography>
                  </Box>

                  <Box>
                    <AppointmentTypeChip appointmentType={appt.appointment_type} />
                  </Box>

                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    <Chip
                      icon={<AccessTimeIcon sx={{ fontSize: '14px !important', color: 'success.main !important' }} />}
                      label={`${mins}min en consulta`}
                      size="small"
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, color: 'success.main', bgcolor: 'success.50' }}
                    />
                    {features?.billing && paymentSummary[appt.id] && (
                      <Chip
                        label={paymentSummary[appt.id].label}
                        size="small"
                        clickable
                        onClick={() => setPaymentApptDialog(appt)}
                        color={PAYMENT_STATUS_COLOR[paymentSummary[appt.id].status] || 'default'}
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                      />
                    )}
                  </Stack>

                  {/* "Ver ficha" — antes esta sección no tenía ningún
                      accionable, el único camino a la ficha era hacer click
                      en el nombre. Pedido explícito del usuario
                      (2026-11-04): acción explícita para retomar/ver la
                      consulta en curso, mismo destino que ya usa "Llamar"
                      (onCallPatient, con el contexto de carga de HC).
                      Label genérico ("Ver ficha", no "Ver HC") porque este
                      botón es el mismo para los 3 roles y recepcionista no
                      ve historia clínica en esa pantalla — iría a
                      "Turnos" en vez de "Consultas" (ver PatientProfile.js/
                      PatientSidebar.js, canSeeClinicalInfo). "Cobrar" al
                      lado — pedido explícito del usuario (2026-11-04):
                      justo en consulta es cuando recepción suele cobrar. */}
                  <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                    {canManage && features?.billing && appt.patient_id && (
                      <Tooltip title="Cobrar / gestionar pagos">
                        <IconButton size="small" color="success" onClick={() => setPaymentApptDialog(appt)}>
                          <PaidIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onCallPatient && (
                      <Tooltip title="Ver ficha del paciente">
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<EditNoteIcon fontSize="small" />}
                          onClick={() => onCallPatient(appt)}
                        >
                          Ver ficha
                        </Button>
                      </Tooltip>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Edit note dialog */}
      <Dialog open={!!noteDialog} onClose={() => setNoteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Nota de recepción</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            placeholder="Ej: está borracho, tiene acompañante, trae estudios..."
            value={noteDialog?.note || ''}
            onChange={(e) => setNoteDialog((d) => ({ ...d, note: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveNote} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de doble llamado — ver callPatient() */}
      <Dialog open={!!doubleCallConfirm} onClose={() => setDoubleCallConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>¿Llamar a un segundo paciente?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {doubleCallConfirm?.appt?.doctor?.name} ya está en consulta con{' '}
            <strong>{doubleCallConfirm?.otherPatientName}</strong>. ¿Confirmás llamar también a{' '}
            <strong>{doubleCallConfirm?.appt?.patient?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDoubleCallConfirm(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => {
              const { appt } = doubleCallConfirm;
              setDoubleCallConfirm(null);
              doCallPatient(appt);
            }}
          >
            Llamar igual
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gestionar pagos — reusa AppointmentDialog en modo edición, abierto
          directo en la sección de Pagos (ver initialSection), en vez de
          duplicar el CRUD de pagos en un popup aparte. patients/waTemplates
          vacíos: estas secciones (crear paciente nuevo, WhatsApp) no se
          necesitan desde este atajo, solo Pagos — mismo criterio que ya usa
          Dashboard.js para su diálogo de "turno nuevo". */}
      {paymentApptDialog && (
        <AppointmentDialog
          open={!!paymentApptDialog}
          onClose={() => setPaymentApptDialog(null)}
          isEdit
          initialSection="billing"
          selectedEvent={paymentApptDialog}
          doctorId={paymentApptDialog.doctor_id}
          doctor={paymentApptDialog.doctor}
          patients={[]}
          setPatients={() => {}}
          waTemplates={[]}
          onSaved={() => { setPaymentApptDialog(null); fetch(); }}
        />
      )}
    </Box>
  );
}
